import { Box, Grid, Typography, Button, CircularProgress, Skeleton } from '@mui/material'
import React, { useEffect, useState, useCallback } from 'react'
import dayjs from 'dayjs'
import { useAppSelector, useAppDispatch } from '../../../../store'
import { fetchRewardsActivity, selectRewardsActivity, setRewardsActivityPage, resetRewardsActivity } from '../../../../store/profileSlice'
import { PointsHistoryItemDTO } from '../../../../types/pointsHistoryTypes'
import PointTransactionCard from '../../../../components/leaderboard/pointTransaction/PointTransactionCard'
import PointTransactionTable from '../../../../components/leaderboard/pointTransaction/PointTransactionTable'
import { IPointList } from '../../../../types/leaderboardTypes'
import { fetchWalletBalance, WalletBalanceResponse } from '../../../../services/rewardsService'

interface PointTransactionProps {
  propName?: string
}

/**
 * Safely formats timestamp to date string using dayjs
 * Returns "—" if timestamp is missing or invalid
 */
const formatDateTime = (timestamp: string | null | undefined): string => {
  if (!timestamp) return '—'
  
  try {
    const date = dayjs(timestamp)
    if (!date.isValid()) {
      return '—'
    }
    return date.format('D MMM YYYY')
  } catch {
    return '—'
  }
}

/**
 * Temporary fallback mapping for missing pointsDelta
 * TODO: Remove once backend consistently sends pointsDelta
 */
const getFallbackPointsDelta = (item: PointsHistoryItemDTO): number | null => {
  // If pointsDelta exists, use it
  if (item.pointsDelta !== undefined && item.pointsDelta !== null) {
    return item.pointsDelta
  }
  
  // Temporary fallback: infer from eventType/label for known actions
  const label = (item.label || item.eventType || '').toLowerCase()
  const eventType = (item.eventType || '').toLowerCase()
  
  // Known earning events (positive delta)
  if (
    label.includes('scan') ||
    label.includes('menu') ||
    label.includes('receipt') ||
    label.includes('review') ||
    label.includes('referral') ||
    label.includes('invite') ||
    label.includes('instagram') ||
    label.includes('newsletter') ||
    eventType.includes('earn') ||
    eventType.includes('bonus')
  ) {
    // Return a placeholder positive value (backend should provide actual value)
    return null // Keep as null to show "—" until backend provides real value
  }
  
  // Known spending events (negative delta)
  if (
    label.includes('redeem') ||
    label.includes('spend') ||
    eventType.includes('redeem') ||
    eventType.includes('spend')
  ) {
    // Return a placeholder negative value (backend should provide actual value)
    return null // Keep as null to show "—" until backend provides real value
  }
  
  return null
}

/**
 * Formats points delta with +/- prefix, or — if null
 */
const formatPointsDelta = (pointsDelta: number | null | undefined, item?: PointsHistoryItemDTO): string => {
  // Try fallback if pointsDelta is missing
  const delta = pointsDelta !== undefined && pointsDelta !== null 
    ? pointsDelta 
    : (item ? getFallbackPointsDelta(item) : null)
  
  if (delta === undefined || delta === null) return '—'
  if (delta > 0) return `+${delta}`
  if (delta < 0) return `${delta}`
  return '0'
}

/**
 * Converts PointsHistoryItemDTO to IPointList format for table
 */
const convertToPointList = (items: PointsHistoryItemDTO[]): IPointList[] => {
  return items.map((item) => {
    const date = formatDateTime(item.timestamp)
    const label = item.label || item.eventType || 'Activity'
    const points = formatPointsDelta(item.pointsDelta, item)
    
    return {
      date,
      action: label,
      points,
    }
  })
}

const PointTransaction: React.FC<PointTransactionProps> = ({ propName }) => {
  const dispatch = useAppDispatch()
  const rewardsActivity = useAppSelector(selectRewardsActivity)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Get wallet balance which includes monthly progress data
  const [walletBalance, setWalletBalance] = useState<WalletBalanceResponse | null>(null)
  
  useEffect(() => {
    const loadBalance = async (): Promise<void> => {
      try {
        const balance = await fetchWalletBalance()
        setWalletBalance(balance)
      } catch (error) {
        // Fallback to null if balance fetch fails
        setWalletBalance(null)
      }
    }
    void loadBalance()
  }, [])
  
  // Calculate summary card - show monthly progress points earned this month
  const summaryData = React.useMemo(() => {
    // Use monthlyProgressPoints from backend (points earned this calendar month)
    // Fallback to 0 if missing (older backend)
    const earned = walletBalance?.monthlyProgressPoints ?? 0

    return [
      {
        image: 'coin-success',
        coin: earned,
        subtitle: 'Points earned this month',
        color: 'success.50',
      },
    ]
  }, [walletBalance?.monthlyProgressPoints])

  // Lazy load: only fetch when component mounts (tab is opened for first time)
  useEffect(() => {
    const shouldFetch = !hasLoadedOnce && !rewardsActivity.loading
    const hasCache = rewardsActivity.activities.length > 0
    
    if (shouldFetch) {
      if (hasCache) {
        // Use cached data, don't fetch
        setHasLoadedOnce(true)
        setIsInitialLoad(false)
      } else {
        // Fetch fresh data with initial params: page=0, limit=50
        setHasLoadedOnce(true)
        setIsInitialLoad(true)
        dispatch(fetchRewardsActivity({ page: 0, limit: 50 }))
          .finally(() => setIsInitialLoad(false))
      }
    }
  }, [dispatch, hasLoadedOnce, rewardsActivity.loading, rewardsActivity.activities.length])

  // Handle load more - increment page and append items
  const handleLoadMore = useCallback(() => {
    if (!rewardsActivity.loading && rewardsActivity.hasMore) {
      const nextPage = rewardsActivity.page + 1
      dispatch(setRewardsActivityPage(nextPage))
      // eslint-disable-next-line
      dispatch(fetchRewardsActivity({ page: nextPage, limit: 50 }))
    }
  }, [dispatch, rewardsActivity.loading, rewardsActivity.hasMore, rewardsActivity.page])

  // Handle refresh - reset and fetch from page 0
  const handleRefresh = useCallback(() => {
    dispatch(resetRewardsActivity())
    dispatch(setRewardsActivityPage(0))
    dispatch(fetchRewardsActivity({ page: 0, limit: 50, refresh: true }))
      .finally(() => {
        setHasLoadedOnce(true)
      })
  }, [dispatch])

  // Convert activities to point list format
  // Activities should already be normalized in PointsHistoryItemDTO format from the store
  const normalizedActivities = rewardsActivity.activities as PointsHistoryItemDTO[]
  const pointList = convertToPointList(normalizedActivities)

  return (
    <Box my={{ xs: 2.5, md: 5 }}>
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }}>
          {summaryData.map((item, index) => (
            <Grid item xs={12} key={index}>
              <PointTransactionCard data={item} />
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box mt={5}>
        {isInitialLoad && rewardsActivity.loading ? (
          <Box>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 1 }} />
            ))}
          </Box>
        ) : rewardsActivity.error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {rewardsActivity.error}
            </Typography>
            <Button variant="contained" onClick={handleRefresh}>
              Try Again
            </Button>
          </Box>
        ) : pointList.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No activity yet.
            </Typography>
          </Box>
        ) : (
          <>
            <PointTransactionTable pointList={pointList} />
            {rewardsActivity.hasMore && (
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={rewardsActivity.loading}
                >
                  {rewardsActivity.loading ? <CircularProgress size={20} /> : 'Load More'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default PointTransaction
